import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { resolveImageUrl } from './ImageUpload';
import { useCategories } from '../context/CategoriesContext';

const isInternal = (href) => href && href.startsWith('/');

/**
 * FeaturedSlideshow — clean, full-bleed TV-screen with selectable transitions.
 * Admin picks one of: fade | slide | zoom | kenburns | flip | blur
 */
const FeaturedSlideshow = ({ slides, autoplaySeconds = 5, transition = 'kenburns', wrapperClassName = '' }) => {
  const { categories } = useCategories();
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);

  const count = slides.length;
  const seconds = Math.max(1, Number(autoplaySeconds) || 5);
  const tx = ['fade', 'slide', 'zoom', 'kenburns', 'flip', 'blur'].includes(transition)
    ? transition
    : 'kenburns';

  const resolveSlide = useCallback(
    (slide) => {
      const linkedCategory = slide.link?.startsWith('/category/')
        ? categories.find((c) => c.slug === slide.link.replace('/category/', ''))
        : null;
      return {
        ...slide,
        _image:
          resolveImageUrl(slide.image) ||
          resolveImageUrl(linkedCategory?.image) ||
          resolveImageUrl(categories[categories.length - 1]?.image),
        _bg: slide.bg || linkedCategory?.bg || '#E0F2FE',
        _color: slide.color || linkedCategory?.color || '#0EA5E9',
      };
    },
    [categories],
  );

  useEffect(() => {
    if (count < 2) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % count), seconds * 1000);
    return () => clearInterval(id);
  }, [count, seconds]);

  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [count, index]);

  if (count === 0) return null;

  const goTo = (i) => setIndex(((i % count) + count) % count);
  const next = () => goTo(index + 1);
  const prev = () => goTo(index - 1);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchStartX.current = null;
  };

  const current = resolveSlide(slides[index]);
  const CardWrap = isInternal(current.link) ? Link : 'a';

  // Per-transition inactive style (the active slide is always plain opacity-100)
  const inactiveStyles = {
    none: 'opacity-0',
    fade: 'opacity-0',
    slide: 'opacity-0 translate-x-full',
    zoom: 'opacity-0 scale-110',
    kenburns: 'opacity-0 scale-110',
    flip: 'opacity-0 [transform:rotateY(90deg)]',
    blur: 'opacity-0 blur-lg scale-105',
  };
  const activeStyles = {
    none: 'opacity-100',
    fade: 'opacity-100',
    slide: 'opacity-100 translate-x-0',
    zoom: 'opacity-100 scale-100',
    kenburns: 'opacity-100 scale-100',
    flip: 'opacity-100 [transform:rotateY(0deg)]',
    blur: 'opacity-100 blur-0 scale-100',
  };

  return (
    <CardWrap
      to={isInternal(current.link) ? current.link : undefined}
      href={!isInternal(current.link) ? current.link : undefined}
      className={`relative lg:col-span-5 group block overflow-hidden aspect-square bg-slate-900 rounded-3xl ${wrapperClassName}`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="featured-slideshow"
      data-transition={tx}
      aria-label={current.title || 'Featured slide'}
      style={{ perspective: tx === 'flip' ? '1200px' : undefined }}
    >
      <div className="relative h-full aspect-square overflow-hidden">
        {slides.map((s, i) => {
          const r = resolveSlide(s);
          const isActive = i === index;
          return (
            <div
              key={r.id || i}
              className={`absolute inset-0 ${
                tx === 'none' ? '' : 'transition-all duration-700 ease-out will-change-transform'
              } ${isActive ? activeStyles[tx] : inactiveStyles[tx]} ${isActive ? 'z-10' : 'z-0'}`}
              style={{
                background: r._bg,
                transformStyle: tx === 'flip' ? 'preserve-3d' : undefined,
              }}
              data-testid={`featured-slide-${i}`}
              aria-hidden={!isActive}
            >
              {r._image && (
                <img
                  src={r._image}
                  alt=""
                  className={`absolute inset-0 w-full h-full object-cover will-change-transform ${
                    isActive && tx === 'kenburns' ? 'animate-ken-burns' : ''
                  }`}
                  draggable="false"
                />
              )}
            </div>
          );
        })}

        {count > 1 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/5 z-20 overflow-hidden">
            <div
              key={`prog-${index}`}
              className="h-full bg-slate-900/40"
              style={{ animation: `slide-progress ${seconds}s linear forwards` }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes ken-burns {
          0%   { transform: scale(1)    translate3d(0%, 0%, 0); }
          100% { transform: scale(1.08) translate3d(-1.5%, -1%, 0); }
        }
        .animate-ken-burns {
          animation: ken-burns ${seconds + 1.2}s ease-in-out both;
        }
        @keyframes slow-drift {
          0%, 100% { transform: translate3d(0,0,0)   scale(1); }
          50%      { transform: translate3d(8%,4%,0) scale(1.05); }
        }
        .animate-slow-drift { animation: slow-drift 8s ease-in-out infinite; }
        @keyframes slide-progress {
          from { transform: translateX(-100%); }
          to   { transform: translateX(0%); }
        }
      `}</style>
    </CardWrap>
  );
};

export default FeaturedSlideshow;
